// Hồ sơ sản phẩm/dịch vụ = TRÍ NHỚ DÀI HẠN cho AI khi đi bình luận/seeding. API model không nhớ gì giữa hai lần gọi → mọi thứ AI
// cần biết về sản phẩm phải được lưu ở đây và GỬI KÈM mỗi lần soạn (qua knowledge của kịch bản): tài liệu gốc do người dùng dán,
// bản tóm tắt AI làm một lần (tiết kiệm token), quy tắc nói/không nói, và "ghi nhớ" tích luỹ (người dùng thêm, hoặc AI đề xuất
// trong khung chat Tự dựng và người dùng chấp nhận).
export interface HoSoSanPham {
  id: string;
  ten: string;
  taiLieu: string; // nguyên văn người dùng dán (mô tả, tính năng, giá, khách hàng…)
  tomTat?: TomTatSanPham; // AI tóm — dùng để gửi kèm thay tài liệu dài
  quyTac: string[]; // "chỉ nhắc app ở cuối câu", "không nói giá"…
  ghiNho: string[]; // bài học tích luỹ: "khách hỏi nhiều về bản miễn phí", "tránh chữ 'rẻ'"…
  anh?: string[]; // ảnh sản phẩm (data URL JPEG/PNG ≤1024px, tối đa ANH_HO_SO_TOI_DA) — AI ĐỌC khi tóm tắt; không gửi kèm mỗi lần soạn (đắt)
  capNhatLuc: string;
}

export interface TomTatSanPham {
  tom_tat: string; // 2–4 câu
  diem_noi_bat: string[]; // 3–6 ý, mỗi ý 1 dòng — mỗi bình luận chỉ rút 1 ý
  khach_muc_tieu: string;
  khong_noi: string[]; // điều KHÔNG được nói/bịa (giá, khuyến mãi, so sánh đối thủ…)
  tu_anh?: string[]; // điều AI đọc được từ ảnh (chữ, giá, tên gói…) — cũng là nguồn hợp lệ cho câu soạn và cho lớp kiểm số/giá
}

// Phần đưa vào knowledge của kịch bản: gọn, có cấu trúc để prompt soạn đọc được.
export interface KnowledgeSanPham { san_pham: string; cam_ky_them: string[] }
export function knowledgeTuHoSo(hs: HoSoSanPham): KnowledgeSanPham {
  const t = hs.tomTat;
  const dong: string[] = [`Tên: ${hs.ten}`];
  if (t) {
    dong.push(`Tóm tắt: ${t.tom_tat}`);
    if (t.diem_noi_bat.length) dong.push(`Điểm nổi bật (mỗi bình luận chỉ dùng MỘT ý phù hợp với bài):`, ...t.diem_noi_bat.map((d) => `- ${d}`));
    if (t.khach_muc_tieu) dong.push(`Khách mục tiêu: ${t.khach_muc_tieu}`);
    if (t.tu_anh?.length) dong.push("Đọc được từ ảnh sản phẩm:", ...t.tu_anh.map((d) => `- ${d}`));
  } else if (hs.taiLieu.trim()) {
    dong.push(`Tài liệu (rút gọn): ${hs.taiLieu.trim().replace(/\s+/g, " ").slice(0, 1200)}`);
  }
  if (hs.quyTac.length) dong.push("Quy tắc:", ...hs.quyTac.map((q) => `- ${q}`));
  if (hs.ghiNho.length) dong.push("Ghi nhớ từ các lần trước:", ...hs.ghiNho.slice(-8).map((g) => `- ${g}`));
  const camKy = [
    // L-16: AI từng tự gán cho sản phẩm bản chất khác ("Mepo9 hay chia sẻ mẹo" — thật ra là phần mềm dựng video) → dặn rõ cả "là gì".
    "Chỉ nói điều CÓ trong hồ sơ/mô tả sản phẩm (sản phẩm là gì, làm được gì, cho ai); không tự gán cho sản phẩm tính chất, công dụng hay loại hình khác; không bịa giá, khuyến mãi, tính năng, số liệu",
    "Chỉ nhắc sản phẩm khi bài/video thật sự liên quan; nhắc nhẹ, không hơn một lần mỗi bình luận",
    ...(t?.khong_noi ?? []).map((k) => `Không nói: ${k}`),
  ];
  return { san_pham: dong.join("\n"), cam_ky_them: camKy };
}

// Lớp kiểm tất định cho câu soạn khi có hồ sơ: số/giá/phần trăm xuất hiện trong câu mà KHÔNG có trong tài liệu → bịa → không đạt.
export function soLaTrongCau(text: string, taiLieu: string): string | null {
  // Ranh giới từ hai đầu (lỗi thật L-6): số dính sau chữ ("Mepo9") không phải giá, đơn vị dính trước chữ ("9 trên", "9 không") không phải đơn vị.
  const SO_GIA = /(?<![\p{L}\p{N}])\d[\d.,]*\s*(?:đồng|đ|₫|vnd|vnđ|k|tr|triệu|nghìn|ngàn|%|usd|\$)(?![\p{L}\p{N}])/giu;
  const so = [...text.matchAll(SO_GIA)].map((m) => m[0]);
  if (!so.length) return null;
  // Cùng một giá viết khác đơn vị (199.000 đồng ↔ 199.000đ, 5 triệu ↔ 5tr, 99 nghìn ↔ 99k) phải khớp nhau.
  const chuanHoa = (s: string) => s.replace(/[.,\s]/g, "").toLowerCase().replace(/đồng|vnđ|vnd|₫/g, "đ").replace(/triệu/g, "tr").replace(/nghìn|ngàn/g, "k");
  // So với TỪNG giá trích từ tài liệu (cùng mẫu số + đơn vị), không tìm chuỗi con trong cả tài liệu: "99.000đ" từng lọt vì là đuôi
  // của "299.000đ", "5%" lọt vì là đuôi của "15%".
  const giaTaiLieu = new Set([...taiLieu.matchAll(SO_GIA)].map((m) => chuanHoa(m[0])));
  const la = so.filter((s) => !giaTaiLieu.has(chuanHoa(s)));
  return la.length ? `nêu số/giá không có trong hồ sơ sản phẩm: ${la.join(", ")}` : null;
}

/** Lý do ghi nhật ký khi nội dung AI soạn không đạt 2 lần → loại mục: kèm CÂU bị loại (≤200 ký tự) để người dùng thấy vì sao (L-6). */
export function lyDoLoaiMuc(loi: string, loi2: string, cau: string): string {
  const c = String(cau ?? "").trim();
  const trich = c ? ` — câu bị loại: "${c.length > 200 ? c.slice(0, 200) + "…" : c}"` : "";
  return `nội dung không đạt 2 lần (${loi}; ${loi2}) — loại mục, không gõ${trich}`;
}

// Giới hạn tài liệu: lưu tối đa / AI tóm tắt đọc bấy nhiêu ký tự đầu. Panel hiện đếm ký tự theo đúng hai số này.
export const HO_SO_TAI_LIEU_TOI_DA = 60_000;
export const HO_SO_TOM_TAT_DOC = 12_000;

// Lời nhắc AI tóm tắt hồ sơ — DÙNG CHUNG cho plugin (background/model.ts) và thư viện sản phẩm trên Boss (server/src/san-pham.ts).
export function promptTomTatSanPham(ten: string, taiLieu: string, coAnh: boolean): { system: string; user: string } {
  const system = `Bạn là bộ TÓM TẮT HỒ SƠ SẢN PHẨM cho việc đi bình luận/seeding tự nhiên trên mạng xã hội (tiếng Việt).
Đọc tài liệu người dùng dán${coAnh ? " VÀ các ảnh kèm (giao diện, bảng giá, tờ rơi…)" : ""}, trả về DUY NHẤT JSON:
{"tom_tat": "2-4 câu, đúng sự thật trong tài liệu${coAnh ? "/ảnh" : ""}", "diem_noi_bat": ["3-6 ý ngắn, mỗi ý một lợi ích/tính năng cụ thể có trong tài liệu${coAnh ? "/ảnh" : ""}"], "khach_muc_tieu": "1 câu", "khong_noi": ["điều KHÔNG nên nói khi bình luận: giá nếu tài liệu không có giá, khuyến mãi, so sánh đối thủ, số liệu không có trong tài liệu"]${coAnh ? `, "tu_anh": ["mỗi ý một điều ĐỌC ĐƯỢC từ ảnh: chữ, giá, tính năng, tên gói… ghi nguyên văn số/giá như trong ảnh"]` : ""}}
Không bịa thêm bất cứ điều gì ngoài tài liệu${coAnh ? " và ảnh; ảnh mờ/không đọc được thì tu_anh để []" : ""}.`;
  return { system, user: `Sản phẩm: ${ten}\n\nTài liệu:\n${taiLieu || "(không có chữ — chỉ có ảnh)"}` };
}

/** Trả lời JSON của AI → TomTatSanPham (null khi thiếu tom_tat). */
export function chuanTomTatSanPham(r: Partial<TomTatSanPham> | null | undefined, coAnh: boolean): TomTatSanPham | null {
  if (!r || typeof r.tom_tat !== "string" || !r.tom_tat.trim()) return null;
  const t: TomTatSanPham = { tom_tat: r.tom_tat.trim(), diem_noi_bat: Array.isArray(r.diem_noi_bat) ? r.diem_noi_bat.map(String).slice(0, 8) : [], khach_muc_tieu: String(r.khach_muc_tieu ?? "").trim(), khong_noi: Array.isArray(r.khong_noi) ? r.khong_noi.map(String).slice(0, 8) : [] };
  if (coAnh && Array.isArray(r.tu_anh)) t.tu_anh = r.tu_anh.map(String).map((x) => x.trim()).filter(Boolean).slice(0, 10);
  return t;
}

export const HO_SO_ID = (): string => `hs-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
